import { useCallback, useEffect, useRef } from 'react'
import { useToast } from '../context/ToastContext'
import { getToken } from '../services/token'

// Delayed-commit undo pattern for delete actions.  The row hides from the UI
// immediately, an "Undo" toast appears for `delay` ms, and the actual DELETE
// fires when the timer expires.  Undo click cancels the timer and restores
// the row.
//
// Design notes:
//   - The optimistic hide is what makes the delete FEEL fast — users don't
//     wait for the network round-trip to see the row disappear.
//   - The 5-second window is the sweet spot: long enough to catch fat-finger
//     mistakes ("did I just delete the wrong one?"), short enough that users
//     don't wonder "did this actually delete?"
//   - Pending deletes flush on component unmount + tab close via
//     fetch({keepalive:true}) so nothing is orphaned when a user navigates
//     away mid-window.
//
// Usage in a page component:
//   const pending = usePendingDelete()
//   const handleDelete = (row) => {
//     pending.schedule({
//       url: `/api/time-entries/${row.id}`,
//       message: 'Time entry deleted',
//       onOptimisticRemove: () => setEntries(e => e.filter(x => x.id !== row.id)),
//       onRestore: () => fetchData(page),   // called on Undo click OR if DELETE fails
//     })
//   }

export function usePendingDelete() {
  const toast = useToast()
  const pending = useRef({})   // key → { url, timer, committed, restore }

  // Fire-and-forget DELETE that survives tab close.  Native fetch keepalive
  // supports up to 64 KB per browser spec — DELETE has no body so we're safe.
  const flushViaKeepalive = useCallback((url) => {
    try {
      const token = getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      fetch(url, { method: 'DELETE', headers, keepalive: true }).catch(() => {})
    } catch { /* swallow — best-effort on tab close */ }
  }, [])

  useEffect(() => {
    const onBeforeUnload = () => {
      Object.values(pending.current).forEach((entry) => {
        if (!entry.committed) flushViaKeepalive(entry.url)
      })
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      // Route change or unmount — flush every pending delete synchronously.
      // Small race window: the next page may fetch data before the DELETE
      // lands.  Acceptable for a 5-second window used on delete-uncommon flows.
      Object.values(pending.current).forEach((entry) => {
        if (!entry.committed) {
          clearTimeout(entry.timer)
          flushViaKeepalive(entry.url)
        }
      })
      pending.current = {}
    }
  }, [flushViaKeepalive])

  const schedule = useCallback(({
    url, message, onOptimisticRemove, onRestore, onDeleteError,
    delay = 5000, toneAsWarning = false
  }) => {
    // Unique key — id-in-url alone collides if user deletes two same-endpoint items
    const key = `${url}::${Date.now()}::${Math.random().toString(36).slice(2, 6)}`

    // Hide immediately for perceived speed
    try { onOptimisticRemove?.() } catch (e) { console.error(e) }

    const commit = async () => {
      const entry = pending.current[key]
      if (!entry || entry.committed) return
      entry.committed = true
      try {
        const token = getToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const res = await fetch(url, { method: 'DELETE', headers })
        if (!res.ok) {
          throw new Error(`DELETE ${res.status}`)
        }
      } catch (err) {
        // Server rejected the delete — restore the row so state matches truth
        try { onRestore?.() } catch (e) { console.error(e) }
        try {
          const msg = (err && err.message) ? err.message : 'Delete failed'
          onDeleteError?.(err)
          if (!onDeleteError) toast.error(`Delete failed — item restored. (${msg})`)
        } catch { /* swallow */ }
      } finally {
        delete pending.current[key]
      }
    }

    const timer = setTimeout(commit, delay)
    pending.current[key] = { url, timer, committed: false }

    toast.custom({
      type: toneAsWarning ? 'warn' : 'custom',
      message: message || 'Deleted',
      duration: delay,
      actions: [{
        label: 'Undo',
        onClick: () => {
          const entry = pending.current[key]
          if (!entry || entry.committed) return
          clearTimeout(entry.timer)
          entry.committed = true
          delete pending.current[key]
          try { onRestore?.() } catch (e) { console.error(e) }
        }
      }]
    })
  }, [toast])

  return { schedule }
}
